import { designTokens } from '@cinch-labs/design-system'
import { GoogleMap, Marker } from '@react-google-maps/api'
import { useCallback, useEffect, useState, useRef } from 'react'
import { getBounds, useGoogleMapsLoader, styles as mapStyles } from './utils'
import { SvgIcon } from '@cinch-labs/design-system'
import styles from './overlay-map.module.css'
import { SiteCard } from '../site-card/site-card'
import cn from 'classnames'
import { FindASiteProps, ListedSite } from '../../find-a-site'

const {
  color: { vividViolet },
} = designTokens.customProperties.ds

interface MobileMapOverlayProps {
  sites: ListedSite[]
  selectedSite: ListedSite
  onClose: () => void
  onSiteCardClick: FindASiteProps['onSiteCardClick']
  location: {
    latitude: number
    longitude: number
    postcode: string
  }
  buttonText: string
  showCinchStoresFreeOnCard: boolean
}

const locationSvgPath =
  'M25.412 16.077c-1.185 3.283-4.63 9.502-6.634 11.864a3 3 0 0 1-4.782-.274c-2.822-4.224-4.677-7.416-6.169-10.87C5.303 10.95 8.945 4.46 14.551 3.21c.631-.14 1.27-.21 1.911-.21 4.288 0 8.127 3.117 9.223 7.371.497 1.93.406 3.823-.273 5.706zM22.78 11.12C22.02 8.17 19.358 6 16.462 6c-.416 0-.837.045-1.259.138-3.427.765-6.527 5.057-4.622 9.469 1.592 3.686 3.67 7.041 5.91 10.393 1.814-2.138 5.066-8.08 6.099-10.941.475-1.318.536-2.596.19-3.94zM16.5 15c-1.493 0-3.01-1.343-3.01-3s1.517-3 3.01-3c1.493 0 2.913 1.5 2.913 3s-1.42 3-2.913 3z'

export const OverlayMap: React.FC<MobileMapOverlayProps> = ({
  sites,
  selectedSite: initialSelectedSite,
  onClose,
  onSiteCardClick,
  location,
  buttonText,
  showCinchStoresFreeOnCard,
}) => {
  const { isLoaded } = useGoogleMapsLoader()
  const [map, setMap] = useState<google.maps.Map>()
  const [selectedSite, setSelectedSite] =
    useState<ListedSite>(initialSelectedSite)
  const [isCardMinimized, setIsCardMinimized] = useState(false)
  const isProgrammaticMove = useRef(true)

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)

    setTimeout(() => {
      isProgrammaticMove.current = false
    }, 1000)
  }, [])

  const handleMapInteraction = useCallback(() => {
    if (isProgrammaticMove.current) {
      return
    }

    setIsCardMinimized((prev) => {
      if (selectedSite && !prev) {
        return true
      }
      return prev
    })
  }, [selectedSite])

  const markers = sites.map((site) => ({
    id: site.siteId,
    lat: site.coordinates.latitude,
    lng: site.coordinates.longitude,
    site,
  }))

  useEffect(() => {
    if (!map || !selectedSite) {
      return
    }

    isProgrammaticMove.current = true

    map.setZoom(12)
    map.panTo({
      lat: selectedSite.coordinates.latitude,
      lng: selectedSite.coordinates.longitude,
    })

    const timeoutId = setTimeout(() => {
      isProgrammaticMove.current = false
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [map, selectedSite])

  useEffect(() => {
    if (!map || selectedSite) {
      return
    }

    isProgrammaticMove.current = true

    const siteBounds = getBounds(markers)
    map.fitBounds(siteBounds)

    const timeoutId = setTimeout(() => {
      isProgrammaticMove.current = false
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [map, sites, selectedSite])

  const onMarkerClick = (
    site: ListedSite,
    event: google.maps.MapMouseEvent,
  ) => {
    if (event.domEvent) {
      event.domEvent.stopPropagation()
    }

    setSelectedSite(site)
    setIsCardMinimized(false)
  }

  if (!isLoaded) {
    return null
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close map"
        >
          <div className={styles.closeIconCircle}>
            <SvgIcon name="cross" />
          </div>
          <div className={styles.closeTextContainer}>
            <span className={styles.closeText}>Close map</span>
          </div>
        </button>
      </div>
      <GoogleMap
        mapContainerStyle={{
          height: '100%',
          width: '100%',
        }}
        options={{
          mapTypeControl: false,
          clickableIcons: false,
          styles: mapStyles,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: 'greedy',
        }}
        onLoad={onLoad}
        zoom={9}
        onDrag={handleMapInteraction}
        onZoomChanged={handleMapInteraction}
      >
        {markers.map((marker) => (
          <Marker
            title={marker.site.siteName}
            icon={
              {
                size: { width: 7, height: 6 },
                anchor: { x: 19 - 7, y: 19 + 6 },
                fillColor: vividViolet,
                fillOpacity: 1,
                strokeColor: vividViolet,
                path: locationSvgPath,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            }
            key={marker.id}
            position={marker}
            onClick={(e) => onMarkerClick(marker.site, e)}
          />
        ))}
        {location && (
          <Marker
            title={location.postcode}
            position={{
              lat: location?.latitude,
              lng: location.longitude,
            }}
          />
        )}
      </GoogleMap>

      <div
        className={cn(styles.siteCardContainer, {
          [styles.siteCardMinimized]: isCardMinimized,
        })}
        onClick={isCardMinimized ? () => setIsCardMinimized(false) : undefined}
      >
        <SiteCard
          site={selectedSite}
          onClick={onSiteCardClick}
          postcode={location.postcode}
          isSelected={true}
          buttonText={buttonText}
          showCinchStoresFreeOnCard={showCinchStoresFreeOnCard}
        />
      </div>
    </div>
  )
}
